import { useEffect, useState } from 'react';

const debounce = <T extends (...args: any[]) => void>(fn: T) => {
  let frame: number | undefined;
  return (...params: Parameters<T>) => {
    if (frame) {
      cancelAnimationFrame(frame);
    }
    frame = requestAnimationFrame(() => {
      fn(...params);
    });
  };
};

export const useScrollPosition = (scrollRef: React.RefObject<HTMLDivElement>) => {
  const [scrollPercent, setScroll] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return undefined;

    const storeScroll = () => {
      const scrollWidth = scrollContainer.scrollWidth - scrollContainer.clientWidth;
      setScroll(scrollWidth > 0 ? scrollContainer.scrollLeft / scrollWidth : 0);
    };

    const debouncedStoreScroll = debounce(storeScroll);
    let scrollEndTimer: NodeJS.Timeout;

    const onScroll = () => {
      debouncedStoreScroll();
      clearTimeout(scrollEndTimer);
      scrollEndTimer = setTimeout(() => {
        setIsScrolling(false);
      }, 150);
    };

    scrollContainer.addEventListener('scroll', onScroll, { passive: true });

    storeScroll();

    return () => {
      scrollContainer.removeEventListener('scroll', onScroll);
      clearTimeout(scrollEndTimer);
    };
  }, [scrollRef]);

  return { scrollPercent, isScrolling, setIsScrolling };
};
